import { Flex, Box, FlexProps, Button } from '@chakra-ui/react'
import React, { MouseEventHandler, RefObject, useEffect, useRef, useState } from 'react'
import ArrowIcon from '@/public/assets/icons/arrow.svg'

export const StepsTrack = ({
  step,
  children,
  wrapperStyles,
  arrowsStyles,
}: {
  step: number
  children: React.ReactElement[]
  wrapperStyles?: FlexProps
  arrowsStyles?: FlexProps
}) => {
  const stepsTrack = useRef<HTMLDivElement>(null)
  const ref = useRef<RefObject<HTMLDivElement | null>[]>([])
  const [isDragging, setIsDragging] = useState(false)
  const [startX, setStartX] = useState(0)
  const [scrollLeft, setScrollLeft] = useState(0)

  useEffect(() => {
    const currentStepPos = ref?.current?.[Math.max(step - 1, 0)]?.current?.offsetLeft
    if (!currentStepPos || !stepsTrack.current) {
      return
    }

    stepsTrack.current.scrollLeft = currentStepPos
  }, [step])

  const handleMouseDown: MouseEventHandler<HTMLDivElement> = (e) => {
    if (!stepsTrack.current) return
    setIsDragging(true)
    setStartX(e.pageX - stepsTrack.current.offsetLeft)
    setScrollLeft(stepsTrack.current.scrollLeft)
    stepsTrack.current.style.scrollBehavior = 'auto'
  }

  const handleMouseMove: MouseEventHandler<HTMLDivElement> = (e) => {
    if (!isDragging || !stepsTrack.current) return
    e.preventDefault()
    const x = e.pageX - stepsTrack.current.offsetLeft
    const walk = x - startX
    stepsTrack.current.scrollLeft = scrollLeft - walk
  }

  const handleMouseUp = () => {
    if (!stepsTrack.current) return
    setIsDragging(false)
    stepsTrack.current.style.scrollBehavior = 'smooth'
  }

  const handleScroll = (direction: number) => {
    if (!stepsTrack.current || !ref.current) {
      return
    }

    const currentScrollLeft = stepsTrack.current.scrollLeft
    const steps = ref.current.map((x) => x.current).filter((x) => !!x)

    const stepIndex =
      direction == 1
        ? steps.findIndex((x) => x.offsetLeft > currentScrollLeft)
        : steps.reverse().findIndex((x) => x.offsetLeft < currentScrollLeft)

    if (stepIndex >= 0) {
      stepsTrack.current.scrollLeft = steps[stepIndex].offsetLeft
    }
  }

  return (
    <Flex
      gap={1}
      pos={'relative'}
      overflow={'hidden'}
      w={'full'}
      justifyContent={'space-between'}
      alignItems={'center'}
      h={'30px'}
      {...wrapperStyles}
    >
      <Button
        variant={'unstyled'}
        onClick={() => handleScroll(-1)}
        cursor={'pointer'}
        pos={'relative'}
        zIndex={10}
        flexShrink={0}
        minW={'0'}
        h={'full'}
      >
        <Flex bg={'rgba(255, 255, 255, 0.12)'} rounded={'lg'} alignItems={'center'} h={'full'} px={1} {...arrowsStyles}>
          <Box w={4}>
            <ArrowIcon />
          </Box>
        </Flex>
      </Button>
      <Box
        ref={stepsTrack}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        w={'100%'}
        h={'full'}
        pos={'relative'}
        overflowX={'scroll'}
        transition={'0.2s'}
        scrollBehavior={'smooth'}
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none',
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none',
        }}
      >
        <Flex gap={1} pos={'absolute'}>
          {children.map((child, index) => {
            return React.cloneElement(child, { key: index, ref: (ref.current[index] ??= { current: null }) } as any)
          })}
        </Flex>
      </Box>
      <Button
        variant={'unstyled'}
        onClick={() => handleScroll(1)}
        cursor={'pointer'}
        pos={'relative'}
        minW={'0'}
        zIndex={10}
        flexShrink={0}
        h={'full'}
      >
        <Flex bg={'rgba(255, 255, 255, 0.12)'} rounded={'lg'} alignItems={'center'} h={'full'} px={1} {...arrowsStyles}>
          <Box w={4} transform={'rotate(180deg)'}>
            <ArrowIcon />
          </Box>
        </Flex>
      </Button>
    </Flex>
  )
}
